import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import LeadDetails from "./LeadDetails";
import LeadFiles from "./LeadFiles";

function LeadDetailsLayout() {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [allLeadsData, setAllLeadsData] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("lead_details");

  const leadId = useMemo(() => params?.selectedID, [params]);

  const buildListPayload = useCallback(() => {
    const page = searchParams.get("page") || 1;
    const query = searchParams.get("query") || undefined;
    const filterBy = searchParams.get("filterBy") || undefined;
    const location = searchParams.get("location") || undefined;
    const sort = searchParams.get("sort") || undefined;
    const order = searchParams.get("order") || undefined;

    const body = { page: Number(page) };
    if (filterBy) body.filterBy = filterBy;
    if (query) body.query = query;
    if (location && location !== "all") body.location = location;
    if (sort && order) {
      body.sort = sort;
      body.order = order;
    }
    return body;
  }, [searchParams]);

  const getLeadList = useCallback(async () => {
    try {
      setLoading(true);
      const body = buildListPayload();
      const { data } = await API.post("/leads", body, {
        headers: authHeader(),
      });

      const list = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.result)
        ? data.result
        : [];

      if (!list.length && data?.success === false) {
        dispatch(
          setNotification({
            message: data?.message || "Failed to fetch leads",
            type: "error",
          })
        );
      }

      setAllLeadsData(list);
      if (leadId && list.length) {
        const match = list.find((item) => `${item?.leadId}` === `${leadId}`);
        if (match) {
          setSelectedLead(match);
        }
      } else if (!leadId && list.length) {
        setSelectedLead(list[0]);
      }

      return list;
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
      setAllLeadsData([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [buildListPayload, dispatch, leadId]);

  const getLeadDetails = useCallback(async () => {
    if (!leadId) return;
    try {
      setLoading(true);
      const { data } = await API.get(`/leads/${leadId}`, {
        headers: authHeader(),
      });
      if (data?.success) {
        setSelectedLead(data?.data || data?.lead || null);
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to fetch lead",
            type: "error",
          })
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch, leadId]);

  const refreshLead = useCallback(async () => {
    if (!leadId) return;
    const list = await getLeadList();
    if (!list?.length) {
      getLeadDetails();
    }
  }, [getLeadDetails, getLeadList, leadId]);

  const previousLead = () => {
    console.log(allLeadsData);

    if (!selectedLead) return;
    const index = allLeadsData.findIndex(
      (item) => `${item?.leadId}` === `${selectedLead?.leadId}`
    );
    if (index > 0) {
      const newLeadId = allLeadsData[index - 1]?.leadId;
      if (newLeadId) {
        navigate(`/lead/details/${newLeadId}?${searchParams.toString()}`);
      }
    }
  };

  const nextLead = () => {
    if (!selectedLead) return;
    const index = allLeadsData.findIndex(
      (item) => `${item?.leadId}` === `${selectedLead?.leadId}`
    );
    if (index >= 0 && index < allLeadsData.length - 1) {
      const newLeadId = allLeadsData[index + 1]?.leadId;
      if (newLeadId) {
        navigate(`/lead/details/${newLeadId}?${searchParams.toString()}`);
      }
    }
  };

  useEffect(() => {
    if (!leadId) return;
    let ignore = false;

    const fetchData = async () => {
      const list = await getLeadList();
      if (!ignore && (!list || !list.length)) {
        await getLeadDetails();
      }
    };

    fetchData();

    return () => {
      ignore = true;
    };
  }, [leadId, getLeadList, getLeadDetails]);

  return (
    <div
      className="flex-grow-1 d-flex flex-column"
      style={{ position: "relative" }}
    >
      {loading && <Spinner />}
      <div
        className="d-flex justify-content-end align-items-center bg-white shadow-lg ps-5 pe-4 py-2 z-3"
        style={{
          borderRadius: "10px",
          position: "fixed",
          top: 0,
          width: "calc(100vw - 200px)",
          marginLeft: 200,
          zIndex: 50,
        }}
      >
        <div className="flex-grow-1">
          <h4 className="d-flex align-items-center">
            <span className="d-inline-block me-2">
              L-{selectedLead?.leadId}
            </span>
            {selectedLead?.leadName?.toUpperCase()}
          </h4>
        </div>
        <button
          onClick={previousLead}
          className="btn btn-success btn-sm my-1 px-2 py-0"
        >
          <i className="mdi mdi-arrow-left-bold fs-4 py-0 my-0"></i>
        </button>
        <button
          onClick={nextLead}
          className="btn btn-success mx-1 btn-sm my-1 px-2 py-0"
        >
          <i className="mdi mdi-arrow-right-bold fs-4 py-0 my-0"></i>
        </button>
        <button
          className="btn btn-danger btn-sm"
          onClick={() => {
            navigate(`/marketing/leads?${searchParams.toString()}`);
          }}
        >
          Back
        </button>
      </div>

      <div className="d-flex px-2" style={{ marginLeft: 260, marginTop: 80 }}>
        <div className=" d-flex flex-column flex-grow-1">
          <div
            className="flex-grow-1 d-flex flex-column mt-2"
            style={{ borderRadius: 10 }}
          >
            <ul className="nav nav-tabs nav-bordered mb-2  d-flex flex-grow-1">
              <li
                onClick={() => setActiveTab("lead_details")}
                className="nav-item"
              >
                <button
                  className={
                    activeTab === "lead_details"
                      ? "nav-link rounded-0 active bg-primary text-white"
                      : "nav-link rounded-0"
                  }
                >
                  <span className="d-none d-md-block">Lead Details</span>
                </button>
              </li>
              <li
                onClick={() => setActiveTab("attachments")}
                className="nav-item"
              >
                <button
                  className={
                    activeTab === "attachments"
                      ? "nav-link rounded-0 active bg-primary text-white"
                      : "nav-link rounded-0"
                  }
                >
                  <span className="d-none d-md-block">Attachments</span>
                </button>
              </li>
            </ul>
            <div className="d-flex flex-grow-1">
              {activeTab === "lead_details" && selectedLead && (
                <LeadDetails
                  selectedLead={selectedLead}
                  refreshFunc={refreshLead}
                />
              )}
              {activeTab === "attachments" && selectedLead && (
                <div
                  className=" d-flex flex-grow-1 bg-white shadow-lg px-4"
                  style={{ borderRadius: 10 }}
                >
                  <LeadFiles selectedLead={selectedLead} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LeadDetailsLayout;
